import { ReactNode, createContext, useEffect, useState } from 'react'

import favouritesStore from '@/stores/favouritesStore'
import { flushCurrentSession } from '../../services/sessionService'
import getCurrentSession from '../../services/sessionService'
import projectsStore from '@/stores/projectStore'

export type Props = {
	deviceType: 'mobile' | 'desktop'
	loadingSession: boolean
	toggleFavourite(trackId: number): void
	clearAllFavourites(): void
	refreshSession(): Promise<void>
} & Session

interface SessionProviderProps {
	children: ReactNode
}

export const SessionContext = createContext<Props | undefined>(undefined)
const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
	const [deviceType, setDeviceType] = useState<'mobile' | 'desktop'>('desktop')
	const [loadingSession, setLoadingSession] = useState(true)
	const [session, setSession] = useState<Session>({
		user: null,
		expires: null,
	} as Session)
	const { setFavouriteTracks } = favouritesStore()
	const { getProjects } = projectsStore()

	const fetchSession = async (): Promise<void> => {
		const sessionData = await getCurrentSession()
		setSession(sessionData)

		if (sessionData?.user) {
			/** Populate user's "library" */
			setFavouriteTracks(sessionData.user.favouriteTracks || [])
			getProjects()
		}

		setLoadingSession(false)
	}

	useEffect(() => {
		fetchSession()

		return () => {
			setSession(null)
		}
	}, [])

	useEffect(() => {
		if (!session?.expires) return

		const checkExpiration = (event: MouseEvent): void => {
			const hasExpired = new Date(session?.expires) < new Date()

			if (!hasExpired) return

			const target = event.target as HTMLElement
			const linkOrButton = target.closest('a, button, input')

			if (linkOrButton) {
				const returnUrl = encodeURIComponent(
					window.location.pathname + window.location.search
				)

				window.location.href = `/login?returnUrl=${returnUrl}`
			}
		}

		document.addEventListener('click', checkExpiration)

		return () => {
			document.removeEventListener('click', checkExpiration)
		}
	}, [session?.expires])

	useEffect(() => {
		setDeviceType(
			window.matchMedia('(hover: none)').matches ||
				window.matchMedia('(any-hover: none)').matches ||
				window.matchMedia('(pointer: coarse)').matches ||
				window.matchMedia('(any-pointer: coarse)').matches
				? 'mobile'
				: 'desktop'
		)
	}, [])

	const toggleFavourite = (trackId: number): void => {
		flushCurrentSession()
		const {
			user: { favouriteTracks },
		} = session

		const isFavourite = favouriteTracks.some(
			(favouriteTrackId) => favouriteTrackId === trackId
		)

		if (isFavourite) {
			setSession({
				...session,
				user: {
					...session.user,
					favouriteTracks: favouriteTracks.filter(
						(favouriteTrackId) => favouriteTrackId !== trackId
					),
				},
			})
		} else {
			setSession({
				...session,
				user: {
					...session.user,
					favouriteTracks: [...favouriteTracks, trackId],
				},
			})
		}
	}

	const clearAllFavourites = (): void => {
		flushCurrentSession()

		setSession({
			...session,
			user: {
				...session.user,
				favouriteTracks: [],
			},
		})
	}

	const refreshSession = async (): Promise<void> => {
		await flushCurrentSession()
		const newSession = await getCurrentSession()
		setSession(newSession)
	}

	const value = {
		...session,
		deviceType,
		loadingSession,
		toggleFavourite,
		clearAllFavourites,
		refreshSession,
	}

	return (
		<SessionContext.Provider value={value}>{children}</SessionContext.Provider>
	)
}

export default SessionProvider
